import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Link } from "react-router-dom";
import apiService from "../../services/api";
import NotificationBell from "../../components/NotificationBell";
import { Select } from "../../arc/components/select/select";
import { SearchField } from "../../arc/components/search-field/search-field";
import { Input } from "../../arc/components/input/input";
import { SortableDataTable } from "../../arc/components/sortable-data-table/sortable-data-table";
import { Skeleton } from "../../arc/components/skeleton/skeleton";
import { ThemeSwitch } from "../../arc/components/theme-switch/theme-switch";
import { applyThemeWithTransition } from "../../utils/themeTransition";

const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .dash-root.dark .dash-header { box-shadow: 0 1px 0 rgba(255,255,255,0.03), 0 2px 20px rgba(0,0,0,0.6); }

  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; }

  /* HEADER */
  .dash-header { background: #082a58; border-bottom: 1px solid rgba(255,255,255,0.07);  position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1560px; margin: 0 auto; padding: 0 28px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .dash-brand { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
  
  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: var(--radius-sm); color: rgba(255,255,255,0.85); transition: all var(--t); margin-right: 4px; }
  .btn-back:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; transform: translateX(-2px); }

  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 13.5px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-family: var(--mono); font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(255,255,255,0.38); }
  .dash-header-right { display: flex; align-items: center; gap: 8px; }

  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.78); cursor: pointer; text-decoration: none; transition: all var(--t); }
  .btn-header:hover { background: rgba(255,255,255,0.13); border-color: rgba(255,255,255,0.22); color: #fff; }

  .user-pill { display: flex; align-items: center; gap: 7px; padding: 5px 12px; background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12); border-radius: 100px; font-size: 12.5px; font-weight: 500; color: rgba(255,255,255,0.85); }
  .online-dot { width: 6px; height: 6px; border-radius: 50%; background: #34d399; box-shadow: 0 0 0 2px rgba(52,211,153,0.25); flex-shrink: 0; }

  /* SETTINGS */
  .settings-wrap { position: relative; }
  .settings-panel { display: none; position: absolute; right: 0; top: calc(100% + 8px); min-width: 210px; background: #0d2d5a; border: 1px solid rgba(255,255,255,0.1); border-radius: var(--radius); box-shadow: 0 12px 40px rgba(0,0,0,0.5); z-index: 200; padding: 4px; }
  .settings-panel.open { display: block; }
  .dash-root.dark .settings-panel { background: #1a1a24; border-color: var(--border); }
  .settings-row { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; font-size: 12.5px; color: rgba(255,255,255,0.7); border-radius: var(--radius-sm); cursor: pointer; transition: background var(--t); }
  .settings-row:hover { background: rgba(255,255,255,0.07); color: #fff; }

  /* TOGGLE */
  .toggle { position: relative; width: 32px; height: 18px; flex-shrink: 0; }
  .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
  .toggle-track { position: absolute; inset: 0; background: rgba(255,255,255,0.2); border-radius: 100px; cursor: pointer; transition: background var(--t); }
  .toggle-track::after { content: ''; position: absolute; width: 14px; height: 14px; left: 2px; top: 2px; background: #fff; border-radius: 50%; box-shadow: 0 1px 4px rgba(0,0,0,0.25); transition: transform 0.2s var(--ease); }
  .toggle input:checked ~ .toggle-track { background: #34d399; }
  .toggle input:checked ~ .toggle-track::after { transform: translateX(14px); }

  /* MAIN LAYOUT */
  .dash-main { max-width: 1560px; margin: 0 auto; padding: 28px; display: flex; flex-direction: column; gap: 20px; }

  /* PANELS */
  .panel { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); overflow: hidden; }
  .panel-header { display: flex; align-items: center; justify-content: space-between; padding: 15px 20px; border-bottom: 1px solid var(--border); background: var(--surface-sub); gap: 12px; }
  .panel-title { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.01em; display: flex; align-items: center; gap: 8px; }
  .panel-title span { font-family: var(--mono); font-size: 11px; color: var(--text-faint); font-weight: 500; }
  .panel-body { padding: 20px; }

  /* ALERTS */
  .alert-success { display: flex; align-items: center; gap: 8px; padding: 12px 16px; background: var(--green-dim); border: 1px solid var(--green-border); border-radius: var(--radius-sm); font-size: 13px; font-weight: 500; color: var(--green); margin-bottom: 4px; }
  .alert-error { display: flex; align-items: center; gap: 8px; padding: 12px 16px; background: var(--red-dim); border: 1px solid var(--red-border); border-radius: var(--radius-sm); font-size: 13px; font-weight: 500; color: var(--red); margin-bottom: 4px; }

  /* FILTERS */
  .filter-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 16px; margin-bottom: 16px; }
  .field-label, .filter-grid label { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-faint); margin-bottom: 6px; display: block; }
  .field-input { width: 100%; padding: 8px 12px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: 13px; outline: none; transition: border-color var(--t), box-shadow var(--t); }
  .field-input::placeholder { color: var(--text-faint); }
  .field-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-dim); }
  select.field-input { cursor: pointer; -webkit-appearance: none; }
  
  .filter-actions { display: flex; gap: 10px; }

  /* BUTTONS */
  .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-height: 36px; padding: 0 16px; border: 1px solid transparent; border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; line-height: 1.45; cursor: pointer; white-space: nowrap; text-decoration: none; outline: none; -webkit-tap-highlight-color: transparent; transition: color var(--t), background var(--t), border-color var(--t), box-shadow var(--t), opacity var(--t); }
  .btn:focus-visible { outline: 2px solid var(--accent-mid); outline-offset: 2px; }
  .btn:disabled { opacity: 0.52; cursor: not-allowed; }
  .btn-sm { min-height: 32px; padding: 0 12px; }
  .btn-md { min-height: 36px; padding: 0 16px; }
  .btn-lg { min-height: 40px; padding: 0 20px; }
  .btn-primary { background: var(--accent); color: #fff; }
  .btn-primary:hover:not(:disabled) { background: var(--accent-mid); }
  .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-secondary { background: transparent; border: 1px solid var(--border); color: var(--text-muted); }
  .btn-secondary:hover { background: var(--surface-sub); color: var(--text); border-color: var(--border-strong); }

  /* ASSIGNMENT BAR */
  .assign-bar { background: var(--accent-dim); border: 1px solid var(--accent-glow); border-radius: var(--radius-lg); padding: 16px 20px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; box-shadow: var(--shadow-sm); }
  .assign-left { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
  .assign-count { font-family: var(--mono); font-size: 12.5px; font-weight: 600; color: var(--accent); letter-spacing: 0.04em; }
  .assign-select { padding: 8px 12px; background: var(--surface); border: 1px solid var(--border-strong); border-radius: var(--radius-sm); color: var(--text); font-family: var(--sans); font-size: 13px; outline: none; cursor: pointer; min-width: 220px; transition: border-color var(--t); }
  .assign-select:focus { border-color: var(--accent); }
  .assign-right { display: flex; gap: 8px; }

  /* TABLE */
  .table-scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; }
  thead tr { border-bottom: 1px solid var(--border); background: var(--surface-sub); }
  th { padding: 12px 16px; text-align: left; font-family: var(--mono); font-size: 10px; letter-spacing: 0.09em; text-transform: uppercase; color: var(--text-faint); font-weight: 500; white-space: nowrap; }
  tbody tr { border-bottom: 1px solid var(--border); background: var(--surface); transition: background var(--t); cursor: pointer; }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background: var(--surface-hover); }
  tbody tr.selected { background: var(--accent-dim); }
  td { padding: 14px 16px; vertical-align: middle; font-size: 13px; }

  .quick-select { display: flex; align-items: center; gap: 8px; }
  .quick-select-label { font-family: var(--mono); font-size: 10.5px; color: var(--text-faint); text-transform: uppercase; letter-spacing: 0.07em; white-space: nowrap; }
  .quick-select-input { width: 68px; padding: 5px 8px; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius-sm); color: var(--text); font-family: var(--mono); font-size: 12px; outline: none; transition: border-color var(--t), box-shadow var(--t); text-align: center; }
  .quick-select-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-dim); }
  .quick-select-input::-webkit-inner-spin-button, .quick-select-input::-webkit-outer-spin-button { -webkit-appearance: none; }
  .btn-quick-select { padding: 5px 12px; background: var(--accent-dim); border: 1px solid var(--accent-glow); border-radius: var(--radius-sm); color: var(--accent); font-family: var(--sans); font-size: 12px; font-weight: 500; cursor: pointer; transition: all var(--t); white-space: nowrap; }
  .btn-quick-select:hover:not(:disabled) { background: var(--accent); color: #fff; }
  .btn-quick-select:disabled { opacity: 0.4; cursor: not-allowed; }
  
  .row-checkbox { width: 15px; height: 15px; accent-color: var(--accent); cursor: pointer; }
  
  .cell-primary { font-size: 13.5px; font-weight: 600; color: var(--text); letter-spacing: -0.01em; margin-bottom: 2px; }
  .cell-sub { font-family: var(--mono); font-size: 11px; color: var(--text-faint); }
  .cell-meta { font-family: var(--mono); font-size: 12px; color: var(--text-muted); }

  .badge-archive { background: var(--amber-dim); color: var(--amber); border: 1px solid var(--amber-border); padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; margin-left: 8px; vertical-align: middle; }

  /* PAGINATION */
  .pagination-bar { display: flex; align-items: center; justify-content: space-between; padding: 14px 20px; border-top: 1px solid var(--border); background: var(--surface-sub); gap: 16px; flex-wrap: wrap; }
  .page-info { font-family: var(--mono); font-size: 11px; color: var(--text-faint); letter-spacing: 0.04em; }
  .page-info span { color: var(--text-muted); font-weight: 500; }
  .page-controls { display: flex; align-items: center; gap: 6px; }
  .btn-page { display: flex; align-items: center; justify-content: center; min-width: 32px; height: 32px; padding: 0 10px; background: transparent; border: 1px solid var(--border); color: var(--text-muted); border-radius: var(--radius-sm); font-family: var(--sans); font-size: 12px; cursor: pointer; transition: all var(--t); font-weight: 500; }
  .btn-page:hover:not(:disabled) { border-color: var(--border-strong); background: var(--surface); color: var(--text); }
  .btn-page:disabled { opacity: 0.3; cursor: not-allowed; }

  /* LOADING / EMPTY */
  .loading-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 72px 24px; gap: 14px; }
  .spinner { width: 32px; height: 32px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .loading-label { font-family: var(--mono); font-size: 10.5px; letter-spacing: 0.09em; text-transform: uppercase; color: var(--text-faint); }
  
  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; text-align: center; }
  .empty-icon { font-size: 32px; margin-bottom: 12px; opacity: 0.3; }
  .empty-title { font-size: 15px; font-weight: 600; color: var(--text-muted); margin-bottom: 6px; }
  .empty-sub { font-size: 13px; color: var(--text-faint); max-width: 340px; line-height: 1.5; }
`;

const CompanyPool = () => {
  const { user, logout } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(new Set()); // company ids
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    period: "",
    sicCode: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
  });
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [quickSelectCount, setQuickSelectCount] = useState("");
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("axo_theme") === "dark",
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    loadCustomers();
    loadCompanies();
  }, []);

  useEffect(() => {
    loadCompanies();
    // Clear selection when filters or page change
    setSelectedKeys(new Set());
  }, [filters, pagination.currentPage]);

  useEffect(() => {
    localStorage.setItem("axo_theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  useEffect(() => {
    if (!settingsOpen) return;
    const handler = () => setSettingsOpen(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [settingsOpen]);

  const loadCustomers = async () => {
    try {
      const response = await apiService.getCustomers();
      const nonParentCustomers = response.data.filter(
        (c) => !c.features.is_parent,
      );
      setCustomers(nonParentCustomers);
    } catch (err) {
      console.error("Failed to load customers:", err);
    }
  };

  const loadCompanies = async () => {
    setLoading(true);
    try {
      const response = await apiService.getUnassignedCompanies({
        page: pagination.currentPage,
        limit: 20,
        ...filters,
      });
      setCompanies(response.data || []);
      setPagination((prev) => ({
        currentPage:
          response.pagination?.currentPage ??
          response.pagination?.page ??
          prev.currentPage,
        totalPages:
          response.pagination?.totalPages ??
          response.pagination?.total_pages ??
          prev.totalPages,
        total: response.pagination?.total ?? prev.total,
      }));
    } catch (err) {
      console.error("Failed to load companies:", err);
    } finally {
      setLoading(false);
    }
  };

  const getCompositeKey = (company) => company.id;

  const handleQuickSelect = async () => {
    const n = parseInt(quickSelectCount, 10);
    if (!n || n <= 0) return;

    if (n <= companies.length) {
      const newKeys = new Set();
      for (let i = 0; i < n; i++) {
        newKeys.add(companies[i].id);
      }
      setSelectedKeys(newKeys);
      setSuccess(`Selected top ${newKeys.size} companies from this page.`);
      setTimeout(() => setSuccess(""), 3000);
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.getUnassignedCompanies({
        page: 1,
        limit: n,
        ...filters,
        idsOnly: true,
      });

      const newKeys = new Set();
      if (response.ids && Array.isArray(response.ids)) {
        response.ids.forEach((id) => newKeys.add(id));
      } else if (response.data && Array.isArray(response.data)) {
        response.data.forEach((c) => newKeys.add(c.id));
      }

      setSelectedKeys(newKeys);
      setSuccess(`Selected top ${newKeys.size} companies across all pages.`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Failed to fetch IDs for quick select:", err);
      setError("Could not select records, please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = (company) => {
    const key = getCompositeKey(company);
    const newKeys = new Set(selectedKeys);
    if (newKeys.has(key)) {
      newKeys.delete(key);
    } else {
      newKeys.add(key);
    }
    setSelectedKeys(newKeys);
  };

  const handleAssignCompanies = async () => {
    if (!selectedCustomer) {
      setError("Please select a customer");
      return;
    }
    if (selectedKeys.size === 0) {
      setError("Please select at least one company");
      return;
    }

    setAssigning(true);
    setError("");
    try {
      await apiService.assignCompaniesToCustomer(
        selectedCustomer,
        [...selectedKeys],
      );

      setSuccess(`Successfully assigned ${selectedKeys.size} companies!`);
      setSelectedKeys(new Set());
      setSelectedCustomer("");
      loadCompanies();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to assign companies");
    } finally {
      setAssigning(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination((p) => ({ ...p, currentPage: 1 }));
    loadCompanies();
  };

  return (
    <>
      <style>{styles}</style>
      <div className={`dash-root${darkMode ? " dark" : ""}`}>
        <header className="dash-header">
          <div className="dash-header-inner">
            <div className="dash-brand">
              <Link
                to="/super-admin"
                className="btn-back"
                title="Back to Dashboard"
              >
                <svg
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </Link>
              <div className="dash-brand-text">
                <div className="dash-brand-name">Unassigned Pool</div>
                <div className="dash-brand-sub">Axo Signal</div>
              </div>
            </div>
            <div className="dash-header-right">
            <NotificationBell />
              <div
                className="settings-wrap"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="btn-header"
                  onClick={() => setSettingsOpen((o) => !o)}
                >
                  <svg
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Settings
                </button>
                <div className={`settings-panel${settingsOpen ? " open" : ""}`}>
                  <div className="settings-row">
                    <span>Dark mode</span>
                    <ThemeSwitch
                      iconOnly
                      theme={darkMode ? "dark" : "light"}
                      onThemeChange={(next, variant, trigger) => applyThemeWithTransition(next, trigger, setDarkMode, variant)}
                    />
                  </div>
                </div>
              </div>
              <div className="user-pill">
                <div className="online-dot" />
                {user?.name}
              </div>
              <button onClick={logout} className="btn-header">
                Logout
              </button>
            </div>
          </div>
        </header>

        <div className="dash-main">
          {success && <div className="alert-success">✓ {success}</div>}
          {error && <div className="alert-error">✕ {error}</div>}

          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">Filters</div>
            </div>
            <div className="panel-body">
              <form onSubmit={handleSearch}>
                <div className="filter-grid">
                  <div>
                    <SearchField
                      label="Search"
                      value={filters.search}
                      onValueChange={(value) =>
                        setFilters({ ...filters, search: value })
                      }
                      placeholder="Company name or number..."
                    />
                  </div>
                  <div>
                    <Select
                      label="Period"
                      className="field-input"
                      placeholder="All Time"
                      value={filters.period}
                      onValueChange={(value) =>
                        setFilters({ ...filters, period: value })
                      }
                      options={[
                        { value: "today", label: "Today" },
                        { value: "7days", label: "Last 7 Days" },
                      ]}
                    />
                  </div>
                  <div>
                    <Input
                      label="SIC Code"
                      type="text"
                      value={filters.sicCode}
                      onChange={(e) =>
                        setFilters({ ...filters, sicCode: e.target.value })
                      }
                      placeholder="e.g. 62020"
                      className="field-input"
                    />
                  </div>
                </div>
                <div className="filter-actions">
                  <button type="submit" className="btn btn-primary">
                    Search Leads
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({
                        search: "",
                        period: "",
                        sicCode: "",
                      });
                      setPagination((p) => ({ ...p, currentPage: 1 }));
                    }}
                    className="btn btn-secondary"
                  >
                    Reset
                  </button>
                </div>
              </form>
            </div>
          </div>

          {selectedKeys.size > 0 && (
            <div className="assign-bar">
              <div className="assign-left">
                <span className="assign-count">
                  {selectedKeys.size} selected
                </span>
                <Select
                  label="Assign to"
                  className="assign-select"
                  placeholder="Select a customer to assign…"
                  value={selectedCustomer}
                  onValueChange={setSelectedCustomer}
                  options={customers.map((customer) => ({
                    value: String(customer.id),
                    label: customer.name,
                  }))}
                />
              </div>
              <div className="assign-right">
                <button
                  onClick={handleAssignCompanies}
                  disabled={assigning || !selectedCustomer}
                  className="btn btn-primary"
                >
                  {assigning ? "Assigning..." : "Assign to Customer"}
                </button>
                <button
                  onClick={() => setSelectedKeys(new Set())}
                  className="btn btn-secondary"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          <div className="panel">
            <div className="panel-header">
              <div className="panel-title">
                Available Companies{" "}
                <span>({pagination.total.toLocaleString()} found)</span>
              </div>
              {companies.length > 0 && (
                <div className="quick-select">
                  <span className="quick-select-label">Quick select</span>
                  <Input
                    label="Quick select count"
                    hideLabel
                    type="number"
                    min="1"
                    max={companies.length}
                    value={quickSelectCount}
                    onChange={(e) => setQuickSelectCount(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleQuickSelect()}
                    placeholder="N"
                    className="quick-select-input"
                  />
                  <button
                    onClick={handleQuickSelect}
                    disabled={
                      !quickSelectCount || parseInt(quickSelectCount, 10) <= 0
                    }
                    className="btn-quick-select"
                  >
                    Select top N
                  </button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="loading-state" style={{ width: "100%" }}>
                <Skeleton lines={6} label="Loading company pool" />
              </div>
            ) : companies.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">✓</div>
                <div className="empty-title">Pool is empty</div>
                <div className="empty-sub">
                  All available companies have been assigned, or no results
                  match your current filters.
                </div>
              </div>
            ) : (
              <>
                <SortableDataTable
                  caption="Available companies"
                  rowKey="id"
                  selectable
                  selectedKeys={[...selectedKeys].map(String)}
                  onSelectionChange={(keys) => setSelectedKeys(new Set(keys))}
                  rows={companies}
                  columns={[
                    {
                      key: "companyName",
                      label: "Company Details",
                      render: (value, row) => (
                        <>
                          <div className="cell-primary">
                            {value}
                            {row.source === "archive" && (
                              <span className="badge-archive">Archived</span>
                            )}
                          </div>
                          <div className="cell-sub">{row.companyNumber}</div>
                        </>
                      ),
                    },
                    {
                      key: "incorporatedAt",
                      label: "Incorporated",
                      render: (value) => (
                        <div className="cell-meta">
                          {new Date(value).toLocaleDateString("en-GB")}
                        </div>
                      ),
                    },
                    {
                      key: "sicCodes",
                      label: "SIC Codes",
                      render: (value) => (
                        <div className="cell-meta">{value || "-"}</div>
                      ),
                    },
                    {
                      key: "locality",
                      label: "Location",
                      render: (value, row) => (
                        <div className="cell-meta">
                          {value || "-"}
                          {row.postcode && `, ${row.postcode}`}
                        </div>
                      ),
                    },
                  ]}
                />
                {pagination.totalPages > 1 && (
                  <div className="pagination-bar">
                    <div className="page-info">
                      Page <span>{pagination.currentPage}</span> of{" "}
                      <span>{pagination.totalPages}</span>
                    </div>
                    <div className="page-controls">
                      <button
                        onClick={() =>
                          setPagination((p) => ({
                            ...p,
                            currentPage: p.currentPage - 1,
                          }))
                        }
                        disabled={pagination.currentPage === 1}
                        className="btn-page"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() =>
                          setPagination((p) => ({
                            ...p,
                            currentPage: p.currentPage + 1,
                          }))
                        }
                        disabled={
                          pagination.currentPage === pagination.totalPages
                        }
                        className="btn-page"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CompanyPool;
